import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

// import { authApi } from '../api/authApi'
import type { User } from '@/types/api.types'
import { AUTH_CONFIG } from '@/utils/constants'

// RTK Query authApi import removed - using TanStack Query hooks

// Auth state interfaces
interface AuthState {
  // User data
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  expiresAt: number | null

  // Auth flow state
  isAuthenticated: boolean
  isLoading: boolean
  requires2FA: boolean
  otpMethod: 'email' | 'sms'

  // 2FA flow data
  maskedContact: {
    email: string
    phone: string
  } | null

  // Login tracking
  loginAttempts: number
  lastLoginAttempt: string | null
  accountLockedUntil: string | null

  // Session data
  sessionId: string | null
  lastActivity: string | null

  // UI state
  error: string | null
  successMessage: string | null
}

// Stored auth shape for localStorage/session restoration

const initialState: AuthState = {
  // User data
  user: null,
  accessToken: null,
  refreshToken: null,
  expiresAt: null,

  // Auth flow state
  isAuthenticated: false,
  isLoading: false,
  requires2FA: false,
  otpMethod: 'email',

  // 2FA flow data
  maskedContact: null,

  // Login tracking
  loginAttempts: 0,
  lastLoginAttempt: null,
  accountLockedUntil: null,

  // Session data
  sessionId: null,
  lastActivity: null,

  // UI state
  error: null,
  successMessage: null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },

    // Set error
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
      state.successMessage = null
    },

    // Set success message
    setSuccessMessage: (state, action: PayloadAction<string | null>) => {
      state.successMessage = action.payload
      state.error = null
    },

    // Clear messages
    clearMessages: (state) => {
      state.error = null
      state.successMessage = null
    },

    // Set 2FA required
    set2FARequired: (
      state,
      action: PayloadAction<{
        maskedEmail: string
        maskedPhone: string
      }>
    ) => {
      state.requires2FA = true
      state.maskedContact = {
        email: action.payload.maskedEmail,
        phone: action.payload.maskedPhone,
      }
    },

    // Set OTP method
    setOTPMethod: (state, action: PayloadAction<'email' | 'sms'>) => {
      state.otpMethod = action.payload
    },

    // ✅ NEW: Set masked contact (for updating from backend response)
    setMaskedContact: (
      state,
      action: PayloadAction<{
        email: string
        phone: string
      }>
    ) => {
      state.maskedContact = {
        email: action.payload.email,
        phone: action.payload.phone,
      }
    },

    // Clear 2FA state
    clear2FAState: (state) => {
      state.requires2FA = false
      state.maskedContact = null
      state.error = null
    },

    // Increment login attempts
    incrementLoginAttempts: (state) => {
      state.loginAttempts += 1
      state.lastLoginAttempt = new Date().toISOString()

      // Lock account after configured attempts
      if (state.loginAttempts >= AUTH_CONFIG.MAX_LOGIN_ATTEMPTS) {
        const lockUntil = new Date(Date.now() + AUTH_CONFIG.LOCKOUT_DURATION)
        state.accountLockedUntil = lockUntil.toISOString()
      }
    },

    // Reset login attempts
    resetLoginAttempts: (state) => {
      state.loginAttempts = 0
      state.lastLoginAttempt = null
      state.accountLockedUntil = null
    },

    // Update last activity
    updateLastActivity: (state) => {
      state.lastActivity = new Date().toISOString()
    },

    // Logout
    logout: (state) => {
      // Reset to initial state
      Object.assign(state, initialState)
    },

    // Restore session from storage
    restoreSession: (
      state,
      action: PayloadAction<{
        user: User
        accessToken: string
        refreshToken: string
        expiresAt: number
      }>
    ) => {
      state.user = action.payload.user
      state.accessToken = action.payload.accessToken
      state.refreshToken = action.payload.refreshToken
      state.expiresAt = action.payload.expiresAt
      state.isAuthenticated = true
      state.lastActivity = new Date().toISOString()
    },
  },
  // TanStack Query hooks handle all API calls and state management
  // Auth slice now only manages local authentication state
})

// Export actions
export const {
  setLoading,
  setError,
  setSuccessMessage,
  clearMessages,
  set2FARequired,
  setOTPMethod,
  setMaskedContact, // ✅ NEW: Export the new action
  clear2FAState,
  incrementLoginAttempts,
  resetLoginAttempts,
  updateLastActivity,
  logout,
  restoreSession,
} = authSlice.actions

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth
export const selectUser = (state: { auth: AuthState }) => state.auth.user
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated
export const selectIsLoading = (state: { auth: AuthState }) => state.auth.isLoading
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error
export const selectRequires2FA = (state: { auth: AuthState }) => state.auth.requires2FA
export const selectOTPMethod = (state: { auth: AuthState }) => state.auth.otpMethod
export const selectMaskedContact = (state: { auth: AuthState }) => state.auth.maskedContact
export const selectLoginAttempts = (state: { auth: AuthState }) => state.auth.loginAttempts
export const selectIsAccountLocked = (state: { auth: AuthState }) => {
  if (!state.auth.accountLockedUntil) return false
  return new Date(state.auth.accountLockedUntil) > new Date()
}

// Auth status is now managed by NextAuth and TanStack Query hooks
// Session persistence is handled by NextAuth providers

export default authSlice.reducer
